import { CapInspector } from './capInspector';
import { fmtAge, orgMatcher, shortKey } from './healthFormat';
import { REJECT_WINDOWS, windowLabel } from './rejectWindows';
import type { CapInspectorState } from './useCapInspector';
import type { RejectSnapshot } from './types';

// Durable reject history from Postgres. Unlike WriteLog (live, in-memory, 429s
// only, wiped on restart) this reads the persisted `trades` audit table, so it
// answers "what actually failed this morning?" across ALL rejection reasons and
// survives redeploys. Window is selectable; the "by reason" rollup makes the
// dominant failure obvious at a glance.
export function RejectedOrders({
  snap,
  err,
  loading,
  windowMs,
  setWindowMs,
  selectedOrg,
  fallbackOrgNames,
  capInspector,
}: {
  snap: RejectSnapshot | null;
  err: string | null;
  loading: boolean;
  windowMs: number;
  setWindowMs: (ms: number) => void;
  selectedOrg: string;
  fallbackOrgNames: Record<string, string>;
  capInspector: CapInspectorState;
}) {
  const orgNames = snap?.orgNames ?? fallbackOrgNames;
  const inSel = orgMatcher(selectedOrg);
  const recent = (snap?.recent ?? []).filter((r) => inSel(r.orgId));
  const byError = snap?.byError ?? [];
  const serverTs = snap?.serverTs ?? Date.now();
  const winLabel = windowLabel(windowMs);

  // Live cap inspector — opened by clicking a trader in the tape. The request
  // lives in useCapInspector so this panel stays a pure view of what it is given.
  const { userId: inspectUser, cap, err: capErr, loading: capLoading, inspect, close: closeInspector } = capInspector;

  return (
    <section className="border-border rounded-lg border p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h2 className="text-foreground text-sm font-semibold">Rejected Kalshi orders</h2>
        <span className="rounded bg-red-600/80 px-1.5 py-0.5 text-[10px] font-bold text-white">DURABLE</span>
        <span className="text-muted-foreground text-[11px]">
          from the trades audit table — every rejection reason, survives relay restarts
        </span>
        <div className="ml-auto flex items-center gap-1">
          {REJECT_WINDOWS.map((w) => (
            <button
              key={w.label}
              onClick={() => setWindowMs(w.ms)}
              className={`rounded border px-2 py-0.5 text-[11px] ${
                w.ms === windowMs
                  ? 'border-orange-400 bg-orange-400/15 text-orange-300'
                  : 'border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {err ? (
        <p className="py-3 text-center text-xs text-red-400">Couldn&apos;t load reject history: {err}</p>
      ) : !snap ? (
        <p className="text-muted-foreground py-3 text-center text-xs">{loading ? 'Loading…' : 'No data.'}</p>
      ) : (
        <>
          <p className="text-muted-foreground mb-3 text-xs">
            {selectedOrg === 'all' ? (
              <>
                <span className={snap.total > 0 ? 'font-semibold text-red-400' : 'font-semibold text-green-400'}>
                  {snap.total}
                </span>{' '}
                rejected fleet-wide in the last {winLabel}
                {snap.shown < snap.total ? ` · showing latest ${snap.shown}` : ''}
              </>
            ) : (
              <>
                <span className={recent.length > 0 ? 'font-semibold text-red-400' : 'font-semibold text-green-400'}>
                  {recent.length}
                </span>{' '}
                shown for this org · {snap.total} fleet-wide in the last {winLabel}
              </>
            )}
          </p>

          {snap.total === 0 ? (
            <p className="text-muted-foreground py-3 text-center text-xs">
              No rejected Kalshi orders in the last {winLabel}. 👍
            </p>
          ) : (
            <div className="space-y-4">
              {/* Rollup by exact error message — fleet-wide over the full window,
                  so a burst's dominant cause is obvious even if the tape is capped. */}
              <div>
                <h3 className="text-muted-foreground mb-1 text-[11px] font-medium uppercase tracking-wide">
                  By reason (fleet, last {winLabel})
                </h3>
                <table className="w-full text-left text-xs">
                  <thead className="text-muted-foreground border-border border-b">
                    <tr>
                      <th className="py-1.5 pr-3 text-right font-medium">Count</th>
                      <th className="px-3 font-medium">Reason</th>
                      <th className="px-3 text-right font-medium">Last</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {byError.map((g, i) => (
                      <tr key={`${g.reason}-${i}`} className="border-border/50 border-b">
                        <td className="text-foreground py-1.5 pr-3 text-right font-semibold">{g.count}</td>
                        <td className="px-3" title={g.sample ?? g.reason}>
                          <span className="text-red-400">{g.reason}</span>
                          {g.sample && g.sample !== g.reason && (
                            <span className="text-muted-foreground ml-2">
                              {g.sample.length > 80 ? `${g.sample.slice(0, 80)}…` : g.sample}
                            </span>
                          )}
                        </td>
                        <td className="text-muted-foreground whitespace-nowrap px-3 text-right">
                          {g.lastTs ? `${fmtAge(Math.max(0, serverTs - g.lastTs))} ago` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Recent tape — respects the org drill-down. */}
              <div>
                <h3 className="text-muted-foreground mb-1 text-[11px] font-medium uppercase tracking-wide">
                  Recent ({recent.length})
                </h3>
                {recent.length === 0 ? (
                  <p className="text-muted-foreground py-2 text-center text-xs">None for this org in-window.</p>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="text-muted-foreground border-border sticky top-0 border-b bg-[var(--background)]">
                        <tr>
                          <th className="py-1.5 pr-3 font-medium">When</th>
                          <th className="px-3 font-medium">Account</th>
                          <th className="px-3 font-medium">Order</th>
                          <th className="px-3 font-medium">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="font-mono">
                        {recent.map((e, i) => {
                          const acct = e.username ?? shortKey(`${e.scope}:${e.userId}`).id;
                          const orgLabel = e.orgId ? orgNames[e.orgId] ?? shortKey(`org:${e.orgId}`).id : null;
                          return (
                            <tr key={`${e.ts}-${i}`} className="border-border/50 border-b">
                              <td className="text-muted-foreground whitespace-nowrap py-1.5 pr-3">
                                {fmtAge(Math.max(0, serverTs - e.ts))} ago
                              </td>
                              <td className="px-3">
                                <span className="text-muted-foreground">{e.scope}</span>
                                <button
                                  onClick={() => inspect(e.userId)}
                                  className="text-foreground hover:text-orange-300 ml-1 underline decoration-dotted underline-offset-2"
                                  title="Inspect this trader's live cap decomposition"
                                >
                                  {acct}
                                </button>
                                {orgLabel && <span className="text-muted-foreground ml-1">· {orgLabel}</span>}
                              </td>
                              <td className="text-foreground whitespace-nowrap px-3">
                                {e.action ?? '—'}
                                {e.side ? ` ${e.side}` : ''}
                                {e.count != null ? ` ×${e.count}` : ''}
                                {e.ticker ? <span className="text-muted-foreground ml-1">{e.ticker}</span> : null}
                              </td>
                              <td className="px-3" title={e.message ?? ''}>
                                <div className="text-red-400">
                                  {e.message
                                    ? e.message.length > 70
                                      ? `${e.message.slice(0, 70)}…`
                                      : e.message
                                    : '—'}
                                </div>
                                {/* Cap-breach decomposition — the reason `current` was
                                    what it was, split into its inflation sources. */}
                                {e.breach && e.breach.rule === 'LIMIT_MAX_CONTRACTS' && (
                                  <div className="text-muted-foreground text-[10px]">
                                    cur {e.breach.current ?? '?'} = open {e.breach.openContracts ?? '?'} · intent{' '}
                                    {e.breach.intentContracts ?? '?'} / cap {e.breach.cap ?? '?'}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {inspectUser && (
                <CapInspector cap={cap} err={capErr} loading={capLoading} onClose={closeInspector} />
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
